import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { currentBusinessId } from "../lib/business";
import { Link, useSearchParams } from "react-router-dom";
import { api, Contact, ContactFilter, CREDIT_USD, creditCost } from "../lib/api";
import EditContactModal from "../components/EditContactModal";
import VerifyCandidatesModal from "../components/VerifyCandidatesModal";
import SendCampaignModal from "../components/SendCampaignModal";

export default function Contacts() {
  const [params, setParams] = useSearchParams();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Filters mirror URL params for shareable links
  const filter: ContactFilter = useMemo(() => ({
    q:                   params.get("q") || "",
    country:             params.get("country") || "",
    type:                params.get("type") || "",
    source:              params.get("source") || "",
    review_status:       params.get("review_status") || "",
    verification_status: params.get("verification_status") || "",
    title_contains:      params.get("title_contains") || "",
    has_email:           (params.get("has_email") as "0" | "1" | "") || "",
    include_deleted:     params.get("include_deleted") === "1",
    page:                Number(params.get("page") || 1),
    limit:               Number(params.get("limit") || 100),
    sort:                params.get("sort") || "id",
    order:               (params.get("order") as "asc" | "desc") || "desc",
  }), [params]);

  // Filter-option dropdowns
  const [countries, setCountries] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const refreshFilterOptions = useCallback(() => {
    api.filters().then(r => { setCountries(r.countries); setTypes(r.types); setSources(r.sources); });
  }, []);
  useEffect(() => { refreshFilterOptions(); }, [refreshFilterOptions]);

  const reload = useCallback(() => {
    setLoading(true); setError(null);
    api.list(filter)
      .then(r => { setContacts(r.contacts); setTotal(r.total); setPages(r.pages); })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
    refreshFilterOptions();
  }, [filter, refreshFilterOptions]);
  useEffect(() => { reload(); setSelected(new Set()); }, [reload]);

  function setF(k: keyof ContactFilter, v: string | boolean | number | undefined) {
    const next = new URLSearchParams(params);
    if (v === "" || v == null || v === false) next.delete(k);
    else next.set(k, typeof v === "boolean" ? (v ? "1" : "0") : String(v));
    if (k !== "page") next.delete("page");
    setParams(next);
  }
  function clearFilters() {
    setParams(new URLSearchParams());
  }

  // Bulk actions
  const [bulkBusy, setBulkBusy] = useState(false);
  async function bulk(action: "delete" | "set_review" | "restore", value?: string) {
    if (!selected.size && !confirmBulkOnFilter(action)) return;
    setBulkBusy(true);
    try {
      const body = selected.size
        ? { ids: [...selected], action, value }
        : { filter: cleanFilter(filter), action, value };
      const r = await api.bulk(body);
      reload();
      setSelected(new Set());
      alert(`${action} → ${r.affected.toLocaleString()} contact(s)`);
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally { setBulkBusy(false); }
  }
  function confirmBulkOnFilter(action: string): boolean {
    if (!total) return false;
    return confirm(`No rows ticked. Apply "${action}" to all ${total.toLocaleString()} matching the current filter?`);
  }

  function exportCsv() {
    const u = new URLSearchParams();
    const f = filter;
    if (f.q) u.set("q", f.q);
    if (f.country) u.set("country", f.country);
    if (f.type) u.set("type", f.type);
    if (f.source) u.set("source", f.source);
    if (f.review_status) u.set("review_status", f.review_status);
    if (f.verification_status) u.set("verification_status", f.verification_status);
    if (f.title_contains) u.set("title_contains", f.title_contains);
    if (f.has_email) u.set("has_email", f.has_email);
    if (f.include_deleted) u.set("include_deleted", "1");
    u.set("business", String(currentBusinessId()));
    window.location.href = `/api/outreach/export?${u.toString()}`;
  }

  async function verifyBulk() {
    // Selection only. Running this off whatever filter happened to be on screen was
    // 200 credits behind one click with nothing ticked.
    if (!selected.size) { alert("Tick the rows you want to verify first."); return; }
    // The server caps a request at 500; say the real number so the cost below is honest.
    const cap = Math.min(selected.size, 500);
    const distinct = new Set(
      contacts.filter(c => selected.has(c.id) && c.email).map(c => (c.email || "").trim().toLowerCase())
    ).size;
    if (!confirm(
      `Verify ${cap} selected contact(s) via NeverBounce?\n\n` +
      `COST: at most ${creditCost(distinct || cap)}\n` +
      `(1 credit = USD $${CREDIT_USD} per address checked)\n\n` +
      `Primary address only — never the alternates. Duplicate addresses are ` +
      `checked once, rows already verified are skipped, and a domain that answers ` +
      `catch-all is not paid for twice, so the real cost is usually lower.`
    )) return;
    setBulkBusy(true);
    try {
      const body = { ids: [...selected], limit: cap };
      const r = await api.verify(body);
      reload();
      setSelected(new Set());
      // Report what it actually cost, not just how many rows changed — those are
      // different numbers once duplicate addresses and dead domains are skipped.
      const used = r.credits_used ?? 0;
      alert(
        [
          `Done — ${r.contacts_touched ?? r.checked} contact(s) settled.`,
          ``,
          `Credits used: ${creditCost(used)}`,
          r.reused ? `Reused an existing result (free): ${r.reused}` : "",
          r.skipped ? `Skipped: ${r.skipped}` : "",
          r.errors ? `Errors: ${r.errors}` : "",
          r.exhausted ? `\n⚠ NeverBounce reports the balance is exhausted — the run stopped early.` : "",
        ].filter(Boolean).join("\n"),
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally { setBulkBusy(false); }
  }

  // Per-row decisions (used by keyboard shortcuts)
  const focusedRow = useRef<number>(-1);
  const decide = useCallback(async (id: number, action: "relevant" | "not_relevant" | "pending" | "delete") => {
    try {
      if (action === "delete") {
        await api.delete(id);
        setContacts(prev => prev.filter(c => c.id !== id));
        setTotal(t => Math.max(0, t - 1));
      } else {
        await api.update(id, { review_status: action });
        setContacts(prev => prev.map(c => c.id === id ? { ...c, review_status: action as Contact["review_status"] } : c));
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }, []);

  // Per-row edit + verify modals
  const [editing,   setEditing]   = useState<Contact | null>(null);
  const [adding,    setAdding]    = useState(false);
  const [verifying, setVerifying] = useState<Contact | null>(null);
  const [sending,   setSending]   = useState(false);
  function saveEdit(updated: Partial<Contact>) {
    if (!editing) return;
    api.update(editing.id, updated)
      .then(() => {
        setContacts(prev => prev.map(c => c.id === editing.id ? { ...c, ...updated } : c));
        setEditing(null);
      })
      .catch(e => alert(e instanceof Error ? e.message : String(e)));
  }
  function addContact(fields: Partial<Contact>) {
    api.create(fields)
      .then(() => { setAdding(false); reload(); })
      .catch(e => alert(e instanceof Error ? e.message : String(e)));
  }
  async function restore(id: number) {
    try {
      await api.restore(id);
      setContacts(prev => prev.map(c => c.id === id ? { ...c, deleted_at: null } : c));
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  }

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (!contacts.length) return;
      if (editing || adding || verifying || sending) return;   // a modal is open — don't act on the list behind it
      const el = document.activeElement as HTMLElement | null;
      if (el && /^(input|textarea|select)$/i.test(el.tagName)) return;

      const key = ev.key.toLowerCase();
      const i = focusedRow.current;
      const row = contacts[i];
      switch (key) {
        case "arrowdown":
        case "j":
          focusedRow.current = Math.min(contacts.length - 1, i + 1); highlightRow();
          ev.preventDefault();
          break;
        case "arrowup":
        case "k":
          focusedRow.current = Math.max(0, i - 1); highlightRow();
          ev.preventDefault();
          break;
        case "r":
          if (row) { decide(row.id, "relevant"); focusedRow.current = Math.min(contacts.length - 1, i + 1); highlightRow(); }
          break;
        case "x":
          if (row) { decide(row.id, "not_relevant"); focusedRow.current = Math.min(contacts.length - 1, i + 1); highlightRow(); }
          break;
        case "delete":
        case "backspace":
          if (row) { decide(row.id, "delete"); /* same index now points to next row */ highlightRow(); }
          break;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [contacts, decide, editing, adding, verifying, sending]);
  function highlightRow() {
    document.querySelectorAll("tr[data-row-focused]").forEach(r => r.removeAttribute("data-row-focused"));
    const tr = document.querySelectorAll("#ocrows tr")[focusedRow.current];
    if (tr) { tr.setAttribute("data-row-focused", "yes"); tr.scrollIntoView({ block: "nearest" }); }
  }

  const allSelected = contacts.length > 0 && contacts.every(c => selected.has(c.id));
  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(contacts.map(c => c.id)));
  }
  function toggleOne(id: number) {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Contacts</h1>
          <p className="mt-1 text-sm text-slate-500">
            {loading ? "Loading…" : `${contacts.length.toLocaleString()} of ${total.toLocaleString()} shown · page ${filter.page} of ${pages}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">Rows per page</label>
          <select
            className="!h-8 !text-xs !py-1 !px-2"
            value={String(filter.limit)}
            onChange={(e) => setF("limit", Number(e.target.value))}
          >
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="250">250</option>
            <option value="500">500</option>
          </select>
          <button className="btn-primary !text-xs !py-1 !px-2.5" onClick={() => setAdding(true)}>+ Add contact</button>
          <button className="btn-secondary !text-xs !py-1 !px-2.5" onClick={exportCsv}>Export CSV</button>
          <Link to="/outreach/review" className="btn-secondary !text-xs !py-1 !px-2.5">Quick review →</Link>
          <Link to="/outreach/import" className="btn-secondary !text-xs !py-1 !px-2.5">Import</Link>
        </div>
      </div>

      {pages > 1 && (
        <Pager page={filter.page || 1} pages={pages} onChange={(p) => setF("page", p)} />
      )}

      <div className="card p-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
          <FilterInput label="Search"
            value={filter.q || ""}
            onChange={(v) => setF("q", v)}
            placeholder="name / company / email…" />
          <FilterInput label="Title contains"
            value={filter.title_contains || ""}
            onChange={(v) => setF("title_contains", v)}
            placeholder="hostess, intern…" />
          <FilterSelect label="Country" value={filter.country || ""} options={countries} onChange={(v) => setF("country", v)} />
          <FilterSelect label="Type" value={filter.type || ""} options={types} onChange={(v) => setF("type", v)} />
          <FilterSelect label="Source" value={filter.source || ""} options={sources} onChange={(v) => setF("source", v)} />
          <FilterSelect label="Review" value={filter.review_status || ""} options={["pending","relevant","not_relevant","done"]} onChange={(v) => setF("review_status", v)} />
          <FilterSelect label="Verification" value={filter.verification_status || ""} options={["unchecked","valid","invalid","risky","catchall","disposable","unknown"]} onChange={(v) => setF("verification_status", v)} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={filter.include_deleted || false} onChange={(e) => setF("include_deleted", e.target.checked)} />
            Show deleted
          </label>
          <FilterSelect inline label="Has email" value={filter.has_email || ""} options={[["1","Yes"],["0","No"]]} onChange={(v) => setF("has_email", v)} />
          <button className="btn-ghost !text-xs !py-1 !px-2" onClick={clearFilters}>Clear filters</button>
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-[11px] text-slate-500">{selected.size} selected</span>
            <button disabled={bulkBusy} className="btn-secondary !text-xs !py-1 !px-2" onClick={() => bulk("set_review", "relevant")}>Mark Relevant</button>
            <button disabled={bulkBusy} className="btn-secondary !text-xs !py-1 !px-2" onClick={() => bulk("set_review", "not_relevant")}>Mark Not relevant</button>
            <button disabled={bulkBusy || selected.size === 0}
                    className="btn-secondary !text-xs !py-1 !px-2 disabled:opacity-40"
                    title={selected.size === 0
                      ? "Tick the rows you want to verify first — this spends NeverBounce credits"
                      : `Run NeverBounce on ${selected.size} selected contact(s)`}
                    onClick={() => verifyBulk()}>
              Verify emails{selected.size > 0 ? ` (${selected.size})` : ""}
            </button>
            <button disabled={bulkBusy || selected.size === 0}
                    className="btn-primary !text-xs !py-1 !px-2"
                    title={selected.size === 0 ? "Tick rows first" : "Send a saved template to the selected contacts"}
                    onClick={() => setSending(true)}>
              Send email{selected.size > 0 ? ` (${selected.size})` : ""}
            </button>
            <button disabled={bulkBusy} className="btn-danger !text-xs !py-1 !px-2" onClick={() => bulk("delete")}>
              {selected.size ? `Delete ${selected.size}` : `Delete all ${total.toLocaleString()} matching`}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="card p-4 text-rose-700">{error}</div>}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="refined [&_tbody_td]:!px-3 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[11px] [&_thead_th]:!px-3 [&_thead_th]:!py-2 [&_tbody_td_div]:!text-[11px] [&_tbody_td_a]:!text-[11px]">
            <thead>
              <tr>
                <th className="cell-check"><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                <th>Name / Country</th>
                <th>Title</th>
                <th>Company</th>
                <th className="w-20">Type</th>
                <th>Email</th>
                <th className="w-24">Verification</th>
                <th className="w-24">Review</th>
                <th className="w-[210px]">Actions</th>
              </tr>
            </thead>
            <tbody id="ocrows">
              {contacts.map((c, idx) => (
                <tr key={c.id}
                    className={c.deleted_at ? "opacity-50" : ""}
                    onClick={() => { focusedRow.current = idx; highlightRow(); }}>
                  <td className="cell-check"><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleOne(c.id)} /></td>
                  <td className="max-w-[14rem]">
                    <div className="text-slate-800 truncate" title={c.name}>
                      {c.profile_url ? <a href={c.profile_url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline">{c.name}</a> : c.name}
                    </div>
                    {c.country && <div className="text-[10.5px] text-slate-400 truncate">{c.country}</div>}
                  </td>
                  <td className="text-slate-700 max-w-[18rem] whitespace-normal leading-snug [overflow-wrap:anywhere]">{c.title}</td>
                  <td className="text-slate-700 max-w-[14rem] truncate" title={c.company || undefined}>{c.company}</td>
                  <td className="text-slate-600 text-[11px]">{c.type}</td>
                  <td className="max-w-[14rem]">
                    <div className="font-mono text-[11px] truncate" title={c.email || undefined}>
                      {c.email ? <span className="text-indigo-700">{c.email}</span> : <span className="text-rose-500">missing</span>}
                    </div>
                    {c.email && <EmailSourceLabel src={c.email_source} verified={c.verification_status} />}
                  </td>
                  <td><VerifPill v={c.verification_status} /></td>
                  <td><ReviewPill v={c.review_status} /></td>
                  <td className="whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditing(c)} className="chip bg-slate-100 text-slate-700 ring-1 ring-slate-200">Edit</button>
                      <button onClick={() => setVerifying(c)}
                              disabled={!c.email && !c.alt_emails}
                              className="chip bg-sky-50 text-sky-700 ring-1 ring-sky-200/60 disabled:opacity-40"
                              title="Run NeverBounce on primary + suggested emails (up to 3)">
                        Verify
                      </button>
                      {c.deleted_at
                        ? <button onClick={() => restore(c.id)} className="chip bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60">Restore</button>
                        : <button onClick={() => decide(c.id, "delete")} className="chip bg-rose-50 text-rose-700 ring-1 ring-rose-200/60">Delete</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && contacts.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">No contacts match the current filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pages > 1 && (
        <Pager page={filter.page || 1} pages={pages} onChange={(p) => setF("page", p)} />
      )}

      <div className="text-[11px] text-slate-500 text-center">
        Per-row buttons: <span className="font-medium text-slate-700">Edit</span> = update fields · <span className="font-medium text-sky-700">Verify</span> = NeverBounce primary + 2 suggestions, pick the winner · <span className="font-medium text-rose-700">Delete</span> = remove.
        Keyboard: <kbd>↑</kbd>/<kbd>↓</kbd> move · <kbd>R</kbd> mark Relevant · <kbd>X</kbd> mark Skip · <kbd>Del</kbd> Delete.
      </div>

      {adding && (
        <EditContactModal
          contact={null}
          onClose={() => setAdding(false)}
          onSave={addContact}
        />
      )}
      {editing && (
        <EditContactModal
          contact={editing}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}
      {verifying && (
        <VerifyCandidatesModal
          contact={verifying}
          onClose={() => setVerifying(null)}
          onPicked={(newEmail, newStatus) => {
            setContacts(prev => prev.map(c => c.id === verifying.id ? { ...c, email: newEmail, verification_status: newStatus, verification_provider: "neverbounce" } : c));
            setVerifying(null);
          }}
          onTested={(newStatus) => {
            setContacts(prev => prev.map(c => c.id === verifying.id ? { ...c, verification_status: newStatus, verification_provider: "neverbounce" } : c));
          }}
        />
      )}
      {sending && (
        <SendCampaignModal
          selected={contacts.filter(c => selected.has(c.id))}
          onClose={() => setSending(false)}
          onSent={() => {
            // The modal shows its own success confirmation; just close + refresh here.
            setSending(false);
            setSelected(new Set());
            reload();
          }}
        />
      )}
    </div>
  );
}

function cleanFilter(f: ContactFilter): ContactFilter {
  const { page, limit, sort, order, ...rest } = f;
  return rest;
}

function Pager({ page, pages, onChange }: { page: number; pages: number; onChange: (p: number) => void }) {
  const win: number[] = [];
  const lo = Math.max(1, page - 2);
  const hi = Math.min(pages, page + 2);
  for (let i = lo; i <= hi; i++) win.push(i);
  return (
    <div className="flex flex-wrap items-center justify-center gap-1 text-xs">
      <button disabled={page <= 1}     className="btn-secondary !text-xs !py-1 !px-2" onClick={() => onChange(1)}>« First</button>
      <button disabled={page <= 1}     className="btn-secondary !text-xs !py-1 !px-2" onClick={() => onChange(page - 1)}>‹ Prev</button>
      {lo > 1 && <span className="px-1 text-slate-400">…</span>}
      {win.map((p) => (
        <button key={p}
                className={p === page
                  ? "btn-primary !text-xs !py-1 !px-2.5"
                  : "btn-secondary !text-xs !py-1 !px-2.5"}
                onClick={() => onChange(p)}>{p}</button>
      ))}
      {hi < pages && <span className="px-1 text-slate-400">…</span>}
      <button disabled={page >= pages} className="btn-secondary !text-xs !py-1 !px-2" onClick={() => onChange(page + 1)}>Next ›</button>
      <button disabled={page >= pages} className="btn-secondary !text-xs !py-1 !px-2" onClick={() => onChange(pages)}>Last »</button>
      <span className="ml-2 text-slate-500">of {pages.toLocaleString()}</span>
    </div>
  );
}

function FilterInput({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      <input
        type="text"
        className="w-full !h-8 !text-xs !py-1.5 !px-2.5"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
function FilterSelect({ label, value, options, onChange, inline }: {
  label: string;
  value: string;
  options: Array<string | [string, string]>;
  onChange: (v: string) => void;
  inline?: boolean;
}) {
  return (
    <div className={inline ? "flex items-center gap-2" : undefined}>
      <label className={inline ? "text-[11px] text-slate-500" : "block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1"}>{label}</label>
      <select
        className={`w-full !h-8 !text-xs !py-1 !px-2 ${inline ? "!w-32" : ""}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">All</option>
        {options.map((o) => Array.isArray(o)
          ? <option key={o[0]} value={o[0]}>{o[1]}</option>
          : <option key={o}    value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function EmailSourceLabel({ src, verified }: { src: string | null | undefined; verified: Contact["verification_status"] }) {
  // If NeverBounce has already verified, that's the strongest signal.
  if (verified === "valid")    return <span className="mt-0.5 inline-block text-[9.5px] font-medium uppercase tracking-wide text-emerald-700">Verified valid</span>;
  if (verified === "invalid")  return <span className="mt-0.5 inline-block text-[9.5px] font-medium uppercase tracking-wide text-rose-700">Verified invalid</span>;
  if (verified === "catchall") return <span className="mt-0.5 inline-block text-[9.5px] font-medium uppercase tracking-wide text-amber-700">Catchall (server accepts anything)</span>;
  if (verified === "disposable") return <span className="mt-0.5 inline-block text-[9.5px] font-medium uppercase tracking-wide text-rose-700">Disposable</span>;
  if (verified === "unknown")  return <span className="mt-0.5 inline-block text-[9.5px] font-medium uppercase tracking-wide text-slate-500">Verified unknown</span>;
  // Otherwise show the enrichment source (what we know about how this email got here).
  const s = (src || "").toLowerCase();
  if (s === "matched")              return <span className="mt-0.5 inline-block text-[9.5px] font-medium uppercase tracking-wide text-emerald-700">From our database</span>;
  if (s.startsWith("suggested-name")) return <span className="mt-0.5 inline-block text-[9.5px] font-medium uppercase tracking-wide text-amber-700">Guess (person changed jobs)</span>;
  if (s.startsWith("suggested-company")) return <span className="mt-0.5 inline-block text-[9.5px] font-medium uppercase tracking-wide text-amber-700">Guess (company pattern)</span>;
  return <span className="mt-0.5 inline-block text-[9.5px] font-medium uppercase tracking-wide text-slate-400">Unverified guess</span>;
}
function VerifPill({ v }: { v: Contact["verification_status"] }) {
  const cls = {
    valid: "pill pill-green",
    invalid: "pill pill-rose",
    risky: "pill pill-amber",
    catchall: "pill pill-amber",
    disposable: "pill pill-rose",
    unknown: "pill pill-slate",
    unchecked: "pill pill-slate",
  }[v];
  return <span className={cls}>{v}</span>;
}
function ReviewPill({ v }: { v: Contact["review_status"] }) {
  const cls = {
    pending: "pill pill-amber",
    relevant: "pill pill-green",
    not_relevant: "pill pill-slate",
    done: "pill pill-blue",
  }[v];
  return <span className={cls}>{v.replace("_", " ")}</span>;
}
